import React from 'react';
import { HiOutlineHashtag, HiOutlineTrash, HiPlus } from 'react-icons/hi';
import { SlackChannelConfig, SlackVariant } from '../../../../../../types/communication';
import TextEdit from '../../../../../molecules/TextEdit';
import SectionCard from '../../shared/SectionCard';

interface SlackDeliveryProps {
    config: SlackChannelConfig;
    disabled: boolean;
    onChange: (config: SlackChannelConfig) => void;
}

type EnvOption = { env: string; label: string; active: string; inactive: string };

const ENVIRONMENTS: EnvOption[] = [
    { env: 'sandbox', label: 'SANDBOX', active: 'bg-blue-500 border-blue-500 text-white', inactive: 'border-blue-300 text-blue-700 hover:bg-blue-50 dark:text-blue-400 dark:border-blue-500' },
    { env: 'dev', label: 'DEV', active: 'bg-green-500 border-green-500 text-white', inactive: 'border-green-300 text-green-700 hover:bg-green-50 dark:text-green-400 dark:border-green-500' },
    { env: 'hml', label: 'HML', active: 'bg-yellow-500 border-yellow-500 text-white', inactive: 'border-yellow-300 text-yellow-700 hover:bg-yellow-50 dark:text-yellow-400 dark:border-yellow-500' },
    { env: 'prd', label: 'PRD', active: 'bg-red-500 border-red-500 text-white', inactive: 'border-red-300 text-red-700 hover:bg-red-50 dark:text-red-400 dark:border-red-500' },
];

const normalizeChannel = (channel: string): string => channel.replace(/^#/, '').trim().toLowerCase();

/**
 * Slack: cada variante mapeia um ou mais ambientes a um canal. Um ambiente só pode pertencer a uma
 * variante (chips mutuamente exclusivos entre variantes). Ambientes sem variante caem no redirect de
 * teste do serviço (guarda fora de produção).
 */
const SlackDelivery: React.FC<SlackDeliveryProps> = ({ config, disabled, onChange }) => {
    const variants: SlackVariant[] = config.variants.length > 0 ? config.variants : [{ channel: '', environments: ['prd'] }];

    const commit = (next: SlackVariant[]) => onChange({ ...config, variants: next });

    const setChannel = (index: number, channel: string) => {
        const next = variants.map((v, i) => (i === index ? { ...v, channel: normalizeChannel(channel) } : v));
        commit(next);
    };

    const toggleEnvironment = (index: number, env: string) => {
        const next = variants.map((v, i) => {
            if (i === index) {
                const environments = v.environments.includes(env)
                    ? v.environments.filter((e) => e !== env)
                    : [...v.environments, env];
                return { ...v, environments };
            }
            return v;
        });
        commit(next);
    };

    const addVariant = () => commit([...variants, { channel: '', environments: [] }]);

    const removeVariant = (index: number) => commit(variants.filter((_, i) => i !== index));

    const usedByOther = (index: number, env: string): boolean =>
        variants.some((v, i) => i !== index && v.environments.includes(env));

    const allEnvironmentsUsed = ENVIRONMENTS.every(({ env }) => variants.some((v) => v.environments.includes(env)));

    return (
        <SectionCard
            title="Canais do Slack por ambiente"
            description="Mapeie cada canal aos ambientes em que ele é usado. Um ambiente sem canal definido cai no redirecionamento de testes do serviço."
            icon={<HiOutlineHashtag size={18} />}
            actions={
                !disabled && !allEnvironmentsUsed ? (
                    <button
                        type="button"
                        onClick={addVariant}
                        className="flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400"
                    >
                        <HiPlus size={14} /> Adicionar canal
                    </button>
                ) : undefined
            }
        >
            <div className="space-y-4">
                {variants.map((variant, index) => (
                    <div key={index} className="rounded-lg border border-gray-200 dark:border-slate-700 p-4">
                        <div className="flex items-start gap-3">
                            <div className="flex-1">
                                <TextEdit
                                    id={`slack-channel-${index}`}
                                    label="Canal (sem #)"
                                    value={variant.channel}
                                    disabled={disabled}
                                    errorMessage={!disabled && !variant.channel.trim() ? 'Obrigatório' : undefined}
                                    helperText="Nome do canal (ex: alerta-cobranca)"
                                    prefix={<HiOutlineHashtag className="text-purple-600 dark:text-purple-400" />}
                                    onChange={(e) => setChannel(index, e.target.value)}
                                />
                            </div>
                            {!disabled && variants.length > 1 && (
                                <button
                                    type="button"
                                    onClick={() => removeVariant(index)}
                                    className="mt-6 p-2 rounded-full text-red-600 hover:bg-red-50 hover:text-red-800 dark:text-red-400 dark:hover:bg-red-900/40"
                                    title="Remover canal"
                                >
                                    <HiOutlineTrash className="w-5 h-5" />
                                </button>
                            )}
                        </div>
                        <div className="flex flex-wrap gap-2 mt-3">
                            {ENVIRONMENTS.map(({ env, label, active, inactive }) => {
                                const isChecked = variant.environments.includes(env);
                                const isBlocked = !isChecked && usedByOther(index, env);
                                return (
                                    <button
                                        key={env}
                                        type="button"
                                        disabled={disabled || isBlocked}
                                        onClick={() => toggleEnvironment(index, env)}
                                        className={`px-3 py-1 text-xs font-semibold rounded-full border-2 transition-all ${isChecked ? active : inactive} ${disabled || isBlocked ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer'}`}
                                        title={isBlocked ? 'Ambiente já usado em outro canal' : undefined}
                                    >
                                        {label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>
        </SectionCard>
    );
};

export default SlackDelivery;
